import SharedMessagesV2 from "@/components/shared/MessagesV2";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { calculateSourcerCommission } from "./DealSourcerDashboard";
import { Clock, DollarSign } from "lucide-react";

const fmtUSD = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function DealSourcerSubmissions() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const lang = (language as "en" | "fr") || "en";
  const [deals, setDeals] = useState<any[]>([]);

  useEffect(() => {
    if (!user?.id) return;
    const q = query(collection(db, "dealSubmissions"), where("sourcedBy", "==", user.id), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => setDeals(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [user?.id]);

  const stageLabel = (stage: string) => {
    const labels: Record<string, {en: string, fr: string}> = {
      submission: { en: "Submitted", fr: "Soumis" },
      pre_evaluation: { en: "Pre-Evaluation", fr: "Pré-évaluation" },
      evaluation: { en: "Evaluation", fr: "Évaluation" },
      deal_monitor: { en: "Deal Monitor", fr: "Deal Monitor" },
      closing: { en: "Closing", fr: "Closing" },
    };
    return labels[stage]?.[lang] || stage;
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold text-[#1D314D]">
        {lang === "fr" ? "Mes deals référés" : "My Referred Deals"}
      </h1>
      {deals.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          {lang === "fr" ? "Aucun deal référé pour l'instant." : "No referred deals yet."}
        </CardContent></Card>
      ) : deals.map(d => (
        <Card key={d.id}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-[#1D314D]">
                {d.tradeName || d.legalName || d.businessName || d.id}
              </CardTitle>
              <Badge variant="outline">{stageLabel(d.currentStage || d.stage || "submission")}</Badge>
            </div>
          </CardHeader>
          <CardContent className="flex gap-6 text-sm">
            <div className="flex items-center gap-1"><Clock className="h-4 w-4 text-muted-foreground" />
              {d.createdAt?.toDate?.()?.toLocaleDateString() || "—"}
            </div>
            <div className="flex items-center gap-1 text-[#CEAB65] font-semibold">
              <DollarSign className="h-4 w-4" />
              {d.askingPrice ? fmtUSD(calculateSourcerCommission(d.askingPrice)) : lang === "fr" ? "En attente" : "Pending"}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}